// src/components/HomePage.js
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

/* ── floating UI card snippets that overlay the hero ── */
const CardReport = () => (
    <div style={{
        background: 'var(--white)', borderRadius: 16,
        border: '1px solid var(--border)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
        padding: '16px 18px', width: 240,
        animation: 'float 5s ease-in-out infinite',
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: 'var(--sage-l)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14,
            }}>📍</div>
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-3)' }}>Recently found</span>
        </div>
        <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>Blue Backpack</p>
        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>Library, Block C · 2h ago</p>
        <div style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px',
            background: 'var(--sage-l)', borderRadius: 8,
        }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--sage)', display: 'block' }} />
            <span style={{ fontSize: 12, color: '#2e5c3a', fontWeight: 500 }}>Available to claim</span>
        </div>
    </div>
);

const CardClaim = () => (
    <div style={{
        background: 'var(--white)', borderRadius: 16,
        border: '1px solid var(--border)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
        padding: '16px 18px', width: 220,
        animation: 'float 6s ease-in-out 1s infinite',
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{
                width: 28, height: 28, borderRadius: 8, background:  '#fde8e4',
                background: '#fde8e4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
            }}>🔍</div>
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-3)' }}>Lost report</span>
        </div>
        <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>iPhone 14 Pro</p>
        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>Cafeteria B · Yesterday</p>
        <div style={{
            height: 3, background: 'var(--cream-dark)', borderRadius: 99, overflow: 'hidden',
        }}>
            <div style={{ height: '100%', width: '60%', background: 'var(--accent)', borderRadius: 99 }} />
        </div>
        <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 5 }}>Verification in progress</p>
    </div>
);

const CardStat = () => (
    <div style={{
        background: 'var(--ink)', color: 'var(--cream)',
        borderRadius: 16, padding: '18px 20px', width: 200,
        animation: 'float 7s ease-in-out 2s infinite',
    }}>
        <p style={{ fontSize: 11, color: 'rgba(242,239,232,0.5)', marginBottom: 8, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Items reunited</p>
        <p style={{ fontFamily: 'var(--serif)', fontSize: 40, lineHeight: 1, marginBottom: 6 }}>340+</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: '#7adb9a' }}>↑ 12% this month</span>
        </div>
    </div>
);

/* ── How It Works step ── */
const Step = ({ n, emoji, title, body, delay }) => (
    <div className="fade-up" style={{ animationDelay: `${delay}ms` }}>
        <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'var(--cream-dark)', border: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, marginBottom: 16,
        }}>{emoji}</div>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-4)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>0{n}</div>
        <h3 style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 400, marginBottom: 10, lineHeight: 1.2 }}>{title}</h3>
        <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7 }}>{body}</p>
    </div>
);

const HomePage = () => {
    const titleRef = useRef(null);

    return (
        <div className="page-wrap noise" style={{ paddingTop: 60 }}>

            {/* ══ HERO ══════════════════════════════════════════ */}
            <section style={{
                position: 'relative', overflow: 'hidden',
                minHeight: 'calc(100vh - 60px)',
                display: 'flex', alignItems: 'center',
                paddingBottom: 80,
            }}>
                {/* Warm background blob */}
                <div style={{
                    position: 'absolute', top: -80, right: -120,
                    width: 640, height: 640, borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(45,91,227,0.07) 0%, transparent 70%)',
                    pointerEvents: 'none',
                }} />
                <div style={{
                    position: 'absolute', bottom: 0, left: '30%',
                    width: 480, height: 480, borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(232,82,42,0.05) 0%, transparent 70%)',
                    pointerEvents: 'none',
                }} />

                <div className="container">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>

                        {/* LEFT — text */}
                        <div>
                            <div className="fade-up" style={{
                                display: 'inline-flex', alignItems: 'center', gap: 7,
                                padding: '5px 12px', borderRadius: 99,
                                background: 'var(--cream-dark)', border: '1px solid var(--border)',
                                marginBottom: 28,
                            }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--sage)', display: 'block' }} />
                                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-2)' }}>LPU Campus · Live</span>
                            </div>

                            <h1 ref={titleRef} className="display fade-up"
                                style={{ fontSize: 'clamp(52px, 6vw, 82px)', color: 'var(--ink)', marginBottom: 28, animationDelay: '80ms' }}>
                                Lost something<br/>
                                <em style={{ color: 'var(--accent)' }}>on campus?</em>
                            </h1>

                            <p className="fade-up" style={{
                                fontSize: 17, color: 'var(--ink-3)', lineHeight: 1.75,
                                maxWidth: 400, marginBottom: 36, animationDelay: '160ms',
                            }}>
                                Lostit connects finders with owners. Report, browse, verify,
                                and reunite — all in one place, built for LPU students.
                            </p>

                            <div className="fade-up" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', animationDelay: '240ms' }}>
                                <Link to="/upload" className="btn btn-ink btn-lg">Report a lost item</Link>
                                <Link to="/dashboard" className="btn btn-outline btn-lg">Browse found items</Link>
                            </div>

                            <div className="fade-up" style={{
                                display: 'flex', alignItems: 'center', gap: 20, marginTop: 40,
                                animationDelay: '320ms',
                            }}>
                                <div>
                                    <span style={{ fontFamily: 'var(--serif)', fontSize: 28, fontWeight: 400 }}>500+</span>
                                    <span style={{ fontSize: 13, color: 'var(--ink-3)', marginLeft: 6 }}>reports filed</span>
                                </div>
                                <div style={{ width: 1, height: 28, background: 'var(--border)' }} />
                                <div>
                                    <span style={{ fontFamily: 'var(--serif)', fontSize: 28, fontWeight: 400 }}>340+</span>
                                    <span style={{ fontSize: 13, color: 'var(--ink-3)', marginLeft: 6 }}>items reunited</span>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT — floating cards stack */}
                        <div className="fade-up" style={{
                            position: 'relative', height: 480, animationDelay: '160ms',
                        }}>
                            {/* big tinted image block (Maze-style) */}
                            <div style={{
                                position: 'absolute', top: 0, left: 40, right: 0, height: 300,
                                borderRadius: 24, overflow: 'hidden',
                                background: 'linear-gradient(135deg, #c5d3f5 0%, #dce6fb 100%)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 72,
                            }}>
                                <span style={{ opacity: 0.5 }}>🏫</span>
                            </div>

                            {/* Floating card 1 */}
                            <div style={{ position: 'absolute', bottom: 80, left: 0, zIndex: 10 }}>
                                <CardReport />
                            </div>

                            {/* Floating card 2 */}
                            <div style={{ position: 'absolute', top: 180, right: -10, zIndex: 10 }}>
                                <CardClaim />
                            </div>

                            {/* Floating stat */}
                            <div style={{ position: 'absolute', bottom: 0, right: 20, zIndex: 10 }}>
                                <CardStat />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ══ HOW IT WORKS ══════════════════════════════════ */}
            <section style={{
                borderTop: '1px solid var(--border)',
                background: 'var(--white)',
                padding: '96px 0',
            }}>
                <div className="container">
                    <div style={{ marginBottom: 64 }}>
                        <div className="label-xs" style={{ marginBottom: 12 }}>Simple. Secure. Fast.</div>
                        <h2 className="display" style={{ fontSize: 'clamp(36px, 4vw, 54px)', maxWidth: 520 }}>
                            How lostit works
                        </h2>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 48 }}>
                        <Step n={1} emoji="📋" delay={0}
                            title="File a report"
                            body="Submit your item name, description, photo, and location in under two minutes."
                        />
                        <Step n={2} emoji="🔎" delay={80}
                            title="Browse the board"
                            body="Scan all reported items publicly. No login needed to browse — only to contact."
                        />
                        <Step n={3} emoji="🔐" delay={160}
                            title="Prove ownership"
                            body="Describe a unique detail only the owner would know. The finder confirms it."
                        />
                        <Step n={4} emoji="🤝" delay={240}
                            title="Meet & collect"
                            body="Exchange contact details, meet on campus, and complete the handover."
                        />
                    </div>
                </div>
            </section>

            {/* ══ CTA ═══════════════════════════════════════════ */}
            <section style={{ padding: '80px 0' }}>
                <div className="container">
                    <div style={{
                        background: 'var(--ink)', borderRadius: 32, padding: '60px 56px',
                        display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                        justifyContent: 'space-between', gap: 32, position: 'relative', overflow: 'hidden',
                    }}>
                        {/* Subtle dot pattern */}
                        <div style={{
                            position: 'absolute', inset: 0, pointerEvents: 'none',
                            backgroundImage: 'radial-gradient(circle, rgba(242,239,232,0.06) 1px, transparent 1px)',
                            backgroundSize: '28px 28px',
                        }} />
                        <div style={{ position: 'relative', zIndex: 1, maxWidth: 440 }}>
                            <h2 className="display" style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', color: 'var(--cream)', marginBottom: 12 }}>
                                Missing something?<br/><em>Someone found it.</em>
                            </h2>
                            <p style={{ color: 'rgba(242,239,232,0.55)', fontSize: 15, lineHeight: 1.7 }}>
                                Check the gallery — a fellow student may already be waiting to return your item.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
                            <Link to="/dashboard" className="btn btn-lg" style={{
                                background: 'var(--cream)', color: 'var(--ink)',
                                borderRadius: 99,
                            }}>Browse the board</Link>
                            <Link to="/upload" className="btn btn-lg" style={{
                                background: 'transparent', color: 'rgba(242,239,232,0.7)',
                                border: '1.5px solid rgba(242,239,232,0.2)',
                                borderRadius: 99,
                            }}>File a report</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ══ FOOTER ════════════════════════════════════════ */}
            <footer style={{
                borderTop: '1px solid var(--border)',
                padding: '40px 0 32px',
            }}>
                <div className="container">
                    <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: 40,
                        justifyContent: 'space-between', marginBottom: 40, alignItems: 'flex-start',
                    }}>
                        <div style={{ maxWidth: 240 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                                <div style={{
                                    width: 24, height: 24, background: 'var(--ink)', borderRadius: 6,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                        <circle cx="5" cy="5" r="3.5" stroke="var(--cream)" strokeWidth="1.2"/>
                                        <path d="M8 8L11 11" stroke="var(--cream)" strokeWidth="1.2" strokeLinecap="round"/>
                                    </svg>
                                </div>
                                <span style={{ fontWeight: 600, fontSize: 14 }}>lostit</span>
                            </div>
                            <p style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>
                                A campus lost & found platform. Built for LPU students.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: 56, flexWrap: 'wrap' }}>
                            {[
                                { title: 'Platform', items: [['/', 'Home'], ['/upload', 'Report item'], ['/dashboard', 'Browse']] },
                                { title: 'Contact', items: [['mailto:help@lostit.in', 'help@lostit.in'], ['#', 'LPU, Phagwara']] },
                            ].map(({ title, items }) => (
                                <div key={title}>
                                    <p className="label-xs" style={{ marginBottom: 14 }}>{title}</p>
                                    {items.map(([href, label]) => (
                                        <div key={label} style={{ marginBottom: 8 }}>
                                            <a href={href} style={{
                                                fontSize: 14, color: 'var(--ink-3)',
                                                textDecoration: 'none', transition: 'color 0.15s',
                                            }}
                                            onMouseEnter={e => e.target.style.color = 'var(--ink)'}
                                            onMouseLeave={e => e.target.style.color = 'var(--ink-3)'}
                                            >{label}</a>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                        <p style={{ fontSize: 13, color: 'var(--ink-4)' }}>© {new Date().getFullYear()} lostit. All rights reserved.</p>
                        <p style={{ fontSize: 13, color: 'var(--ink-4)' }}>Built by Tekkali Ravi Kumar · LPU CSE 2027</p>
                    </div>
                </div>
            </footer>

            <style>{`
                @media (max-width: 860px) {
                    section > .container > div[style*="grid-template-columns: 1fr 1fr"] {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>
        </div>
    );
};
export default HomePage;